import { useCallback, useMemo, useRef, useState } from "react";
import type { TooltipSnapshot } from "../utils/tooltipSnapshot";

export const MAX_COMPARE_POINTS = 4;

interface InternalCompareState {
  comparePoints: TooltipSnapshot[];
  referenceDataIndex: number | null;
  hoverPoint: TooltipSnapshot | null;
  activationTick: number;
}

export interface PinCompareState {
  comparePoints: TooltipSnapshot[];
  referenceDataIndex: number | null;
  hoverPoint: TooltipSnapshot | null;
  activationTick: number;
  isCompareMode: boolean;
  referencePoint: TooltipSnapshot | null;
}

export interface PinCompareActions {
  enterCompareMode: (snap: TooltipSnapshot | null) => void;
  clearCompareMode: () => void;
  setHoverPoint: (snap: TooltipSnapshot | null) => void;
  toggleComparePoint: (snap: TooltipSnapshot | null) => void;
  setReferencePoint: (snap: TooltipSnapshot | null) => void;
  removePoint: (dataIndex: number) => void;
}

function upsertSnapshot(
  points: TooltipSnapshot[],
  snap: TooltipSnapshot,
): TooltipSnapshot[] {
  const idx = points.findIndex((p) => p.dataIndex === snap.dataIndex);
  if (idx < 0) return [...points, snap];
  const next = points.slice();
  next[idx] = snap;
  return next;
}

export function usePinCompareState() {
  const [internal, setInternal] = useState<InternalCompareState>({
    comparePoints: [],
    referenceDataIndex: null,
    hoverPoint: null,
    activationTick: 0,
  });
  const latestHoverRef = useRef<TooltipSnapshot | null>(null);

  const enterCompareMode = useCallback((snap: TooltipSnapshot | null) => {
    if (!snap) return;
    setInternal((prev) => ({
      comparePoints: [snap],
      referenceDataIndex: snap.dataIndex,
      hoverPoint: null,
      activationTick: prev.activationTick + 1,
    }));
  }, []);

  const clearCompareMode = useCallback(() => {
    setInternal((prev) => {
      if (!prev.comparePoints.length && prev.referenceDataIndex === null) {
        return prev;
      }
      return {
        comparePoints: [],
        referenceDataIndex: null,
        hoverPoint: null,
        activationTick: prev.activationTick,
      };
    });
  }, []);

  const setHoverPoint = useCallback((snap: TooltipSnapshot | null) => {
    setInternal((prev) => {
      if (
        prev.hoverPoint?.dataIndex === snap?.dataIndex &&
        prev.hoverPoint?.xName === snap?.xName
      ) {
        return prev;
      }
      return { ...prev, hoverPoint: snap };
    });
  }, []);

  const toggleComparePoint = useCallback((snap: TooltipSnapshot | null) => {
    if (!snap) return;
    setInternal((prev) => {
      const existingIdx = prev.comparePoints.findIndex(
        (p) => p.dataIndex === snap.dataIndex,
      );
      if (existingIdx >= 0) {
        const nextPoints = prev.comparePoints.filter(
          (p) => p.dataIndex !== snap.dataIndex,
        );
        if (nextPoints.length === 0) {
          return {
            ...prev,
            comparePoints: [],
            referenceDataIndex: null,
            hoverPoint: null,
          };
        }
        const nextReference =
          prev.referenceDataIndex === snap.dataIndex ||
          !nextPoints.some((p) => p.dataIndex === prev.referenceDataIndex)
            ? nextPoints[0].dataIndex
            : prev.referenceDataIndex;
        return {
          ...prev,
          comparePoints: nextPoints,
          referenceDataIndex: nextReference,
          hoverPoint:
            prev.hoverPoint?.dataIndex === snap.dataIndex
              ? null
              : prev.hoverPoint,
        };
      }

      if (prev.comparePoints.length >= MAX_COMPARE_POINTS) return prev;
      return {
        ...prev,
        comparePoints: [...prev.comparePoints, snap],
      };
    });
  }, []);

  const setReferencePoint = useCallback((snap: TooltipSnapshot | null) => {
    if (!snap) return;
    setInternal((prev) => {
      let nextPoints = prev.comparePoints;
      if (!nextPoints.some((p) => p.dataIndex === snap.dataIndex)) {
        if (nextPoints.length >= MAX_COMPARE_POINTS) {
          // Keep column order stable when full: replace the last non-reference slot.
          let replaceIdx = nextPoints.length - 1;
          for (let i = nextPoints.length - 1; i >= 0; i -= 1) {
            if (nextPoints[i].dataIndex !== prev.referenceDataIndex) {
              replaceIdx = i;
              break;
            }
          }
          const replaced = nextPoints.slice();
          replaced[replaceIdx] = snap;
          nextPoints = replaced;
        } else {
          nextPoints = [...nextPoints, snap];
        }
      } else {
        nextPoints = upsertSnapshot(nextPoints, snap);
      }
      return {
        ...prev,
        comparePoints: nextPoints,
        referenceDataIndex: snap.dataIndex,
      };
    });
  }, []);

  const removePoint = useCallback((dataIndex: number) => {
    setInternal((prev) => {
      const nextPoints = prev.comparePoints.filter(
        (p) => p.dataIndex !== dataIndex,
      );
      if (nextPoints.length === prev.comparePoints.length) return prev;
      if (nextPoints.length === 0) {
        return {
          ...prev,
          comparePoints: [],
          referenceDataIndex: null,
          hoverPoint: null,
        };
      }
      const nextReference =
        prev.referenceDataIndex === dataIndex ||
        !nextPoints.some((p) => p.dataIndex === prev.referenceDataIndex)
          ? nextPoints[0].dataIndex
          : prev.referenceDataIndex;
      return {
        ...prev,
        comparePoints: nextPoints,
        referenceDataIndex: nextReference,
        hoverPoint:
          prev.hoverPoint?.dataIndex === dataIndex ? null : prev.hoverPoint,
      };
    });
  }, []);

  const state = useMemo<PinCompareState>(() => {
    const referencePoint =
      internal.referenceDataIndex === null
        ? null
        : internal.comparePoints.find(
            (p) => p.dataIndex === internal.referenceDataIndex,
          ) ?? null;
    return {
      comparePoints: internal.comparePoints,
      referenceDataIndex: internal.referenceDataIndex,
      hoverPoint: internal.hoverPoint,
      activationTick: internal.activationTick,
      isCompareMode:
        internal.comparePoints.length > 0 &&
        internal.referenceDataIndex !== null,
      referencePoint,
    };
  }, [internal]);

  const actions = useMemo<PinCompareActions>(
    () => ({
      enterCompareMode,
      clearCompareMode,
      setHoverPoint,
      toggleComparePoint,
      setReferencePoint,
      removePoint,
    }),
    [
      enterCompareMode,
      clearCompareMode,
      setHoverPoint,
      toggleComparePoint,
      setReferencePoint,
      removePoint,
    ],
  );

  return {
    state,
    actions,
    latestHoverRef,
  };
}
